'use client';

import { Pause } from 'lucide-react';
import type { ActiveTimer } from '@shared/types';
import { useActiveTimer, useStopTimer } from '@/hooks/useTimer';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { formatElapsed, isOverEstimate, useElapsedMs } from './LiveElapsed';

// The header surface for the running timer. Shows the active task name
// and a live elapsed counter that ticks locally every second; the
// underlying /timer/active query refetches every 30s to stay honest
// with what the server thinks. Click to stop (= pause; resume creates
// a new time_log row).
//
// When elapsed crosses the task's `timeEstimateMinutes`, the pill
// shifts to a warning hue — subtle nudge, no popup.
export function ActiveTimerPill() {
  const { data: active } = useActiveTimer();
  const stop = useStopTimer();

  if (!active) return null;
  return (
    <Pill
      active={active}
      onStop={() =>
        stop.mutate(undefined, {
          onError: () => toast.error("Couldn't stop the timer"),
        })
      }
      disabled={stop.isPending}
    />
  );
}

// Inner pill — split out so `useElapsedMs` only runs when there's an
// active timer, keeping hook order stable across the on/off transition.
function Pill({
  active,
  onStop,
  disabled,
}: {
  active: ActiveTimer;
  onStop: () => void;
  disabled: boolean;
}) {
  const elapsedMs = useElapsedMs(active.startedAt);
  const over = isOverEstimate(elapsedMs, active.timeEstimateMinutes);

  return (
    <button
      type="button"
      onClick={onStop}
      disabled={disabled}
      aria-label={`Stop timer on ${active.taskName}`}
      className={cn(
        'group inline-flex h-8 items-center gap-2 rounded-full border pl-2.5 pr-1.5 transition duration-fast ease-out-quint disabled:opacity-50',
        over
          ? 'border-warning-border bg-warning-surface hover:border-warning'
          : 'border-hairline bg-surface/60 hover:border-border hover:bg-surface',
      )}
    >
      <span
        aria-hidden
        className="relative flex h-1.5 w-1.5 items-center justify-center"
      >
        <span
          className={cn(
            'absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping',
            over ? 'bg-warning' : 'bg-accent',
          )}
        />
        <span
          className={cn(
            'relative inline-flex h-1.5 w-1.5 rounded-full',
            over ? 'bg-warning' : 'bg-accent',
          )}
        />
      </span>
      <span
        className={cn(
          'hidden max-w-[14ch] truncate text-[13px] font-medium leading-none sm:inline',
          over ? 'text-warning' : 'text-foreground',
        )}
      >
        {active.taskName}
      </span>
      <span
        className={cn(
          'font-mono text-[11px] leading-none tabular-nums',
          over ? 'text-warning' : 'text-muted-foreground',
        )}
      >
        {formatElapsed(elapsedMs)}
      </span>
      <span
        className={cn(
          'flex h-5 w-5 items-center justify-center rounded transition duration-fast ease-out-quint',
          over ? 'text-warning' : 'text-subtle-foreground group-hover:text-foreground',
        )}
      >
        <Pause className="h-3 w-3" strokeWidth={2.2} />
      </span>
    </button>
  );
}
